'use client';
import { Button, PinHolder, Text, Spinner as Loader } from '@intigral/shared';
import styles from '@intigral/shared/styles/pages/account.module.sass';
import Link from 'next/link';
import { i18n } from '@intigral/utils';
import { ChangeEvent, MouseEventHandler, useEffect, useState } from 'react';

export interface PinVerificationProps {
    handleSubmit: (pin: string) => void;
    handleResendPinCode?: () => void;
    handleEditMobile?: () => void;
    mobileNumber: string;
    isLoading: boolean;
    maxPinLength: number;
    pin?: string;
    setPin?: (pin: string) => void;
    countDownSeconds?: number;
    title: string;
    subtitle: string;
    promptMessage: string;
    resendText: string;
    resendWaitText?: string;
    hideSubmitButton?: boolean;
    hideResendSection?: boolean;
    shouldNotShowEditButton?: boolean;
    shouldAutoFocus?: boolean;
    onCountdownEnd?: () => void;
}

export const PinVerification = ({
    handleSubmit,
    isLoading,
    maxPinLength,
    mobileNumber,
    handleEditMobile = () => {
        return;
    },
    handleResendPinCode = () => {
        return;
    },
    pin = '',
    setPin = (pin) => {
        return;
    },
    countDownSeconds = 0,
    title,
    subtitle,
    promptMessage,
    resendText,
    resendWaitText,
    hideSubmitButton = false,
    hideResendSection = false,
    shouldNotShowEditButton,
    shouldAutoFocus,
    onCountdownEnd,
}: PinVerificationProps) => {
    const [countDown, setcountDown] = useState(countDownSeconds);
    const handleOnSubmit = () => {
        handleSubmit(pin);
    };

    useEffect(() => {
        if (countDown > 0) {
            const timerId = setInterval(() => {
                setcountDown((prevSeconds) => {
                    const nextSeconds = Math.max(prevSeconds - 1, 0);
                    if (nextSeconds === 0 && prevSeconds > 0) {
                        onCountdownEnd?.();
                    }
                    return nextSeconds;
                });
            }, 1000);

            return () => clearInterval(timerId);
        }
    }, [countDown, onCountdownEnd]);

    useEffect(() => {
        return () => {
            setPin('');
        };
    }, []);

    const formatCountDown = (totalSeconds: number) => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    const formattedCountDown = formatCountDown(Number(countDown));
    const countDownLabel = resendWaitText
        ? resendWaitText.replace(/\{\{time\}\}/g, formattedCountDown)
        : formattedCountDown;

    const handleEdit = (e) => {
        e.preventDefault();
        handleEditMobile();
    };

    const handleResend = async (e) => {
        e.preventDefault();
        await handleResendPinCode();
        setcountDown(countDownSeconds);
    };

    return (
        <section data-test-id={'pin-verification-section'} key={'pin-verification-section'} className={styles.account}>
            {isLoading && <Loader />}
            <Text as="h1" variant="h1" margin="0">
                {title}
            </Text>
            <Text as="p">
                {subtitle}
                <Text as="p">
                    <span dir="ltr" style={{ padding: '0 10px' }}>
                        {mobileNumber}
                    </span>
                    {!shouldNotShowEditButton && (
                        <Link href="#" className="primary-brand" onClick={handleEdit}>
                            {i18n.t('edit')}
                        </Link>
                    )}
                </Text>
            </Text>
            <Text as="p">{promptMessage}</Text>
            <PinHolder
                inputStyle="input"
                skipDefaultStyles={true}
                renderInput={(props) => <input {...props} data-test-id="pin-input" />}
                value={pin}
                onChange={(updatePin) => {
                    setPin(updatePin);
                }}
                numInputs={maxPinLength ?? 4}
                shouldAutoFocus={shouldAutoFocus}
                inputType="number"
            />

            {!hideSubmitButton && (
                <Button
                    primary
                    data-test-id={'pin-verification-cta'}
                    key={'pin-verification-cta'}
                    disabled={pin?.length < (maxPinLength ?? 4)}
                    onClick={handleOnSubmit}
                >
                    {i18n.t('user_profile.messages.submit')}
                </Button>
            )}
            {!hideResendSection && countDown > 0 && (
                <Text as="p" data-test-id="count-down-timer">
                    {countDownLabel}
                </Text>
            )}

            {!hideResendSection && countDown === 0 && (
                <Text as="p" data-test-id="resend-pin-code" color="brand" margin="0">
                    <Link href="#" className="primary-brand" onClick={handleResend}>
                        {resendText}
                    </Link>
                </Text>
            )}
        </section>
    );
};

export default PinVerification;
